import { To, useBlocker, useNavigate } from 'react-router-dom';
import { useState, useEffect, useCallback } from 'react';

/**
 * A custom React hook that prevents navigating away from a page when specific conditions are met.
 * This is useful for forms with unsaved changes to prompt the user before they navigate away.
 *
 * @param shouldBlock - Boolean flag that determines whether navigation should be blocked.
 *                      Typically set to true when a form has unsaved changes.
 *
 * @returns An object containing:
 *   - showDialog: Boolean indicating whether the confirmation dialog should be displayed
 *   - handleConfirmNavigation: Function to call when user confirms they want to navigate away
 *   - handleCancelNavigation: Function to call when user cancels the navigation
 *   - allowNavigation: Function to call to allow navigation without confirmation (e.g., after saving form)
 *   - navigateSafely: Function to navigate to a specific route bypassing the blocker
 */
export const useNavigationBlocker = (shouldBlock: boolean) => {
  // Controls the visibility of the confirmation dialog
  const [showDialog, setShowDialog] = useState(false);

  // Tracks whether it's safe to navigate away without confirmation
  const [isSafeToNavigate, setIsSafeToNavigate] = useState(false);

  // Hook to programmatically navigate
  const navigate = useNavigate();

  // Use react-router-dom's useBlocker hook to intercept navigation attempts
  const blocker = useBlocker(
    ({ currentLocation, nextLocation }) =>
      // Only block navigation if all conditions are true:
      // 1. It's not explicitly marked as safe to navigate
      // 2. The shouldBlock prop is true (e.g., form has unsaved changes)
      // 3. We're actually changing routes (not just query params on same route)
      !isSafeToNavigate && shouldBlock && currentLocation.pathname !== nextLocation.pathname
  );

  // Show the confirmation dialog when navigation is blocked
  useEffect(() => {
    if (blocker.state === 'blocked') {
      setShowDialog(true);
    }
  }, [blocker.state]);

  /**
   * Handler for when the user confirms they want to navigate away.
   * Hides the dialog and proceeds with the navigation.
   */
  const handleConfirmNavigation = () => {
    setShowDialog(false);
    blocker.proceed?.();
  };

  /**
   * Handler for when the user cancels the navigation.
   * Hides the dialog and resets the navigation blocker.
   */
  const handleCancelNavigation = () => {
    setShowDialog(false);
    blocker.reset?.();
  };

  /**
   * Sets the internal state to allow navigation without confirmation.
   * Useful for scenarios like after successfully saving a form.
   */
  const allowNavigation = useCallback(() => {
    setIsSafeToNavigate(true);
  }, []);

  /**
   * Navigates to a specified route while bypassing the navigation blocker.
   *
   * @param to - The route to navigate to
   * @returns A cleanup function that cancels navigation if the component unmounts
   *          before the navigation occurs
   */
  const navigateSafely = useCallback(
    (to: To) => {
      setIsSafeToNavigate(true);
      const timeoutId = setTimeout(() => {
        navigate(to);
      }, 0);

      return () => clearTimeout(timeoutId);
    },
    [navigate]
  );

  return {
    showDialog,
    handleConfirmNavigation,
    handleCancelNavigation,
    allowNavigation,
    navigateSafely,
  };
};
